/**
 * QuestionCard — one collapsed question row in the builder's slot list (WCR
 * parity). Shows the type/required badges, an options preview, and
 * conditional/eligibility/calculation indicators; edit + delete actions and a
 * drag handle. ReorderControls carries the move affordances (up/down on every
 * viewport, plus top/bottom/jump-to-position) — HTML5 drag is desktop-only and
 * one-slot-at-a-time, which does not scale to a 40-question form.
 */
import { GripVertical, Pencil, Trash2, EyeOff, AlertCircle, Calculator } from 'lucide-react';
import { FormQuestionDef, normalizeShowIf } from '../../types';
import { typeLabel } from './formMeta';
import ReorderControls from './ReorderControls';

interface QuestionCardProps {
  question: FormQuestionDef;
  /** 1-based display number. */
  number: number;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** 1-based slot position + list length, for the move controls. */
  slotPosition: number;
  slotCount: number;
  onMoveTo: (position: number) => void;
}

export default function QuestionCard({
  question, number, canEdit, onEdit, onDelete, slotPosition, slotCount, onMoveTo,
}: QuestionCardProps) {
  const isCalc = question.type === 'calculation';
  const showIf = normalizeShowIf(question.show_if);
  const hasEligibility = !!question.eligibility
    && !!(question.eligibility.exclude_if || question.eligibility.flag_for_review_if);
  const options = question.options || [];

  return (
    <div className="group flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow duration-150 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      {/* Drag handle (desktop) */}
      {canEdit && (
        <div className="hidden cursor-grab pt-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 sm:block" title="Drag to reorder">
          <GripVertical className="h-5 w-5" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-400">Q{number}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {typeLabel(question.type)}
          </span>
          {!isCalc && question.required !== false && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              Required
            </span>
          )}
        </div>

        <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {question.label || <span className="italic text-gray-400">(untitled question)</span>}
        </p>
        {question.description && (
          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{question.description}</p>
        )}

        {options.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {options.slice(0, 4).map((o, i) => (
              <span key={i} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {o.label}
              </span>
            ))}
            {options.length > 4 && (
              <span className="text-xs text-gray-400">+{options.length - 4} more</span>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3">
          {isCalc && (
            <span className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
              <Calculator className="h-3 w-3" /> Hidden from patients
            </span>
          )}
          {showIf && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <EyeOff className="h-3 w-3" />
              {showIf.conditions.length > 1
                ? `Conditional (${showIf.match} of ${showIf.conditions.length})`
                : 'Conditional'}
            </span>
          )}
          {hasEligibility && (
            <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400">
              <AlertCircle className="h-3 w-3" /> Has eligibility rules
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="flex shrink-0 items-start gap-0.5">
          <ReorderControls position={slotPosition} total={slotCount} onMoveTo={onMoveTo} what="question" />
          <button
            onClick={onEdit}
            className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            aria-label="Edit question"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            aria-label="Delete question"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
